import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { defaultVoiceValuesNoSoprano, Voice, VoiceValues } from '../../../common/src/model.ts';
import { capitalize } from '../../../common/src/util.ts';
import { LocalStorageKey, usabilityTooltipDisplayCount } from '../data/client_local_storage.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { SpeechBubble, SpeechBubbleOrientation } from '../util/speech_bubble.tsx';
import { useLongPress } from '../util/use_long_press.tsx';
import { sliderButtonWidth, SliderLabel, VoiceButtonWrapper } from './player_util.tsx';
import { SequencerInterface } from './sequencer.tsx';

export function VoiceButton({
  isSelected,
  sequencer,
  setSelectedVoice,
  setVoiceValues,
  voice,
  voices,
}: {
  isSelected: boolean;
  sequencer: SequencerInterface;
  setSelectedVoice: (voice: Voice | undefined) => void;
  setVoiceValues: (voiceValues: VoiceValues | ((currentValue: VoiceValues) => VoiceValues)) => void;
  voice: Voice;
  voices: Voice[];
}) {
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const [isMixerVisible] = useLocalStorage(LocalStorageKey.IsMixerVisible);

  const onLongPress = () => {
    if (sequencer.isPlaying) {
      return;
    }
    sequencer.playFirstNote(voice);
  };

  const onClick = useCallback(() => {
    if (isSelected) {
      setSelectedVoice(undefined);
      setVoiceValues({ ...defaultVoiceValuesNoSoprano, [voice]: 1 });
    } else {
      setSelectedVoice(voice);
      setVoiceValues({
        ...Object.fromEntries(voices.map((otherVoiceName) => [otherVoiceName, 0])),
        [voice]: 1,
      });
    }
  }, [isSelected, setSelectedVoice, setVoiceValues, voice, voices]);

  const handleInput = useLongPress(onLongPress, onClick);

  const [tooltipCount, setTooltipCount] = useLocalStorage(LocalStorageKey.TooltipVisibilityCount);
  useEffect(() => {
    if (!isPlayerMaximized || !isMixerVisible || voice !== Voice.Soprano) {
      return;
    }
    setTooltipCount((tooltipCount) =>
      (tooltipCount.longPress ?? 0) < usabilityTooltipDisplayCount + 1
        ? { ...tooltipCount, longPress: (tooltipCount.longPress ?? 0) + 1 }
        : tooltipCount,
    );
  }, [isPlayerMaximized, isMixerVisible, setTooltipCount, voice]);

  return (
    <VoiceButtonWrapper className={isSelected ? 'selectedVoice' : ''} {...handleInput}>
      {isPlayerMaximized &&
        isMixerVisible &&
        voice === Voice.Soprano &&
        (tooltipCount.longPress ?? 0) <= usabilityTooltipDisplayCount && (
          <SpeechBubble $orientation={SpeechBubbleOrientation.Horizontal} $verticalOffset={'15px'}>
            <LongPressTooltip>Press and hold for first note</LongPressTooltip>
          </SpeechBubble>
        )}
      <VoiceSliderLabel>{capitalize(voice)}</VoiceSliderLabel>
    </VoiceButtonWrapper>
  );
}

const VoiceSliderLabel = styled(SliderLabel)`
  width: calc(${sliderButtonWidth} - 5px);
  padding: 5px;
  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: larger;
  text-align: center;
`;

const LongPressTooltip = styled.div`
  width: 16ch;
  font-family: Jost, sans-serif;
  padding: 0.5rem 1rem;
  text-align: center;
`;
